// Sdílený stav klienta pexesa (poslední pohled ze serveru + kurzor) a české texty.
import { ANIMAL_BY_ID, type CardView, type PexesoView } from "../../../games/pexeso/meta.ts";

export interface Model {
  me: string;
  view: PexesoView | null;
  cursor: number;
  sendAction(action: unknown): void;
}

export function animalName(id: string | undefined): string {
  return (id && ANIMAL_BY_ID[id]?.name) || "neznámé";
}

export function rowCol(view: PexesoView, index: number) {
  return { row: Math.floor(index / view.cols), col: index % view.cols };
}

export function posText(view: PexesoView, index: number): string {
  const { row, col } = rowCol(view, index);
  return `řádek ${row + 1}, sloupec ${col + 1}`;
}

export function cardText(card: CardView): string {
  if (card.state === "down") return "zakrytá";
  if (card.state === "up") return `otočená, ${animalName(card.animal)}`;
  return `nalezená, ${animalName(card.animal)}`;
}

export function playerName(view: PexesoView, id: string, me: string): string {
  if (id === me) return "ty";
  return view.players.find((p) => p.id === id)?.name ?? "soupeř";
}

export function scoreText(view: PexesoView): string {
  return "Skóre: " + view.players.map((p) => `${p.name} ${p.score}`).join(", ") + ".";
}

export function turnText(view: PexesoView, me: string): string {
  if (view.turn === me) return "Jsi na tahu.";
  return `Na tahu je ${playerName(view, view.turn, me)}.`;
}

export function remainingPairs(view: PexesoView): number {
  return view.cards.filter((c) => c.state !== "matched").length / 2;
}

export function resultText(view: PexesoView, me: string): string {
  const score = scoreText(view);
  if (view.players.length === 1) return `Konec hry. Všechny páry nalezeny. ${score}`;
  if (view.winners.length > 1) return `Konec hry. Remíza. ${score}`;
  const w = view.winners[0];
  return w === me ? `Konec hry. Vítěz: ty! ${score}` : `Konec hry. Vítěz: ${playerName(view, w, me)}. ${score}`;
}

/** Panoráma podle sloupce: první sloupec vlevo, poslední vpravo. */
export function panFor(view: PexesoView, index: number): number {
  const { col } = rowCol(view, index);
  return view.cols > 1 ? ((col / (view.cols - 1)) * 2 - 1) * 0.9 : 0;
}

/** „Na tahu Jirka, 2 páry; pak ty, 1 pár.“ Pořadí od hráče na tahu. */
export function orderText(view: PexesoView, me: string): string {
  const n = view.players.length;
  const start = Math.max(0, view.players.findIndex((p) => p.id === view.turn));
  const pairs = (k: number) => `${k} ${k === 1 ? "pár" : k >= 2 && k <= 4 ? "páry" : "párů"}`;
  return (
    Array.from({ length: n }, (_, k) => view.players[(start + k) % n])
      .map((p, k) => `${k === 0 ? "Na tahu" : "pak"} ${p.id === me ? "ty" : p.name}, ${pairs(p.score)}`)
      .join("; ") + "."
  );
}

/** Kdo bude na tahu po současném hráči (null, když hraje jen jeden). */
export function nextPlayer(view: PexesoView): string | null {
  const n = view.players.length;
  if (n < 2) return null;
  const i = view.players.findIndex((p) => p.id === view.turn);
  return view.players[(i + 1) % n].id;
}
